import { useState } from 'react';
import { Box, Stack } from '@mui/material';
import {
  CasBaseDialog,
  CasChip,
  CasDisplayField,
  CasLoaderWithBackdrop,
  CasStackRow,
  CasTabsView,
} from 'src/compass/shared/components';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';
import { useRetrieveDetailsById } from 'src/compass/shared/hooks/reactQuery/useRetrieveDetailsById';
import { AccountsTab, OwnersTab, ContactsTab } from 'src/compass/application';
import { Terms } from 'src/compass/shared/util/terms';
import HubspotLink from 'src/compass/application/entityMaintenance/HubspotLink';
import RenderTouchpointFrequency, { useTouchpointCache } from './RenderTouchpointFrequency';
import TagChips from './TagChips';

interface IProps {
  id: number;
  onClose: (dataUpdated?: boolean) => void;
}

export default function CustomerDialog({ id, onClose }: IProps) {
  const { updateTouchpointDetails } = useTouchpointCache();
  const [dataUpdated, setDataUpdated] = useState<boolean>(false);

  const { data, isLoading, isRefetching, isError, refetch } = useRetrieveDetailsById({
    entity: 'Utilities/EntityTouchpoint',
    id,
  });

  if (isLoading) return <CasLoaderWithBackdrop />;
  if (isError) throw new Error('Error retrieving data from the server.');

  const tabList = [
    { label: 'Contacts', children: <ContactsTab entityId={id} data={data?.contacts} onUpdated={refetch} /> },
    { label: 'Accounts', children: <AccountsTab entityId={id} readOnly /> },
    { label: 'Owners', children: <OwnersTab entityId={id} readOnly /> },
  ];

  return (
    <CasBaseDialog
      fullScreen
      maxWidth="xl"
      open
      onClose={() => onClose(dataUpdated)}
      title={
        data && (
          <Stack spacing={0.5}>
            <span>{`${Terms.Customer.label}: ${data?.name}`}</span>
            <TagChips
              entityId={id}
              tags={data?.tags ?? []}
              onChanged={() => setDataUpdated(true)}
            />
          </Stack>
        )
      }
      showCloseIcon
    >
      {isRefetching && <CasLoaderWithBackdrop /> }
      <CasStackRow>
        <CasDisplayField
          label="Next Touchpoint"
          value={
            data?._isPending ? '(updating)' : formatDate(data?.nextTouchpointDate, DateFormat.MDY)
          }
        />
        <CasDisplayField
          label="Frequency"
          value={
            <RenderTouchpointFrequency
              id={id}
              defaultValue={data.touchpointFrequency}
              onUpdated={(nextTouchpointDate) => {
                updateTouchpointDetails({ id, nextTouchpointDate });
                setDataUpdated(true);
              }}
            />
          }
        />
        <CasDisplayField
          label="Last Touchpoint"
          value={formatDate(data?.lastTouchpointDate, DateFormat.MDY)}
        />
        <CasDisplayField
          label="Risk Managers"
          value={
            <CasStackRow>
              {data?.riskManagerNames?.map((riskManager: string) => (
                <CasChip key={riskManager} label={riskManager} color="disabled" />
              ))}
            </CasStackRow>
          }
        />
      </CasStackRow>
      {data?.companyName && (
        <CasDisplayField
          label={
            <CasStackRow alignItems="center">
              <HubspotLink id={data?.companyId} recordPath="record/0-2" />
              {Terms.CRM.label} Company
            </CasStackRow>
          }
          value={data.companyName}
        />
      )}
      {data?.hasHandoffRequest && (
        <Box
          sx={{
            backgroundColor: '#a9ffa9',
            width: 'fit-content',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.875rem',
          }}
        >
          {`Handoff req by ${data?.handoffRequestedByName}`}
        </Box>
      )}
      <CasTabsView tabList={tabList} />
    </CasBaseDialog>
  );
}
